import React, { useRef, useState } from "react";
import { IoClose } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import { backendUrl } from "../../api/constants";
import Button from "../Button/Button";
import EditVenue from "../ui/EditVenue";

const stripNameFromAddress = (name, address) => {
  if (!address) return "address unavailable";
  if (!name) return address;

  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const stripped = address
    .replace(new RegExp(`^${escaped}\\s*,\\s*`, "i"), "")
    .trim();

  return stripped || address;
};

const ClaimedVenues = ({
  userVenues,
  userLocation,
  venues,
  refreshAll,
  refreshVenues,
  selectedVenue,
  setSelectedVenue,
  handleVenuPick,
  refreshControl = null,
}) => {
  const nav = useNavigate();
  const editVenueRef = useRef();
  const [loading, setLoading] = useState(false);
  const [postError, setPostError] = useState();
  const formattedVenues = (userVenues || []).map((venue) => ({
    ...venue,
    name: venue?.name || "untitled venue",
    venueType: venue?.venueType || "venue",
    address: stripNameFromAddress(venue?.name, venue?.address),
    createdAt: venue?.createdAt
      ? new Date(venue.createdAt).toLocaleString()
      : "date unavailable",
    createdAtCompact: venue?.createdAt
      ? new Date(venue.createdAt).toLocaleString(undefined, {
          month: "numeric",
          day: "numeric",
          year: "2-digit",
          hour: "numeric",
          minute: "2-digit",
        })
      : "date unavailable",
  }));

  const handleInputChange = (e) => {
    setSelectedVenue((prev) => ({
      ...prev,
      venue: {
        ...prev.venue,
        [e.target.name]: e.target.value,
      },
    }));
  };

  const updateVenue = async () => {
    setLoading(true);

    const { form } = editVenueRef.current?.getFormData() || {
      form: new FormData(),
    };
    form.append("venue_id", selectedVenue.venue?._id?.toString());

    try {
      const { data } = await axiosInstance.post(
        `${backendUrl}/business/update`,
        form,
        {
          "Content-Type": "multipart/form-data",
        }
      );

      if (data.error) {
        if (typeof data.error === "string") {
          setPostError(data.error);
        } else {
          for (const key in data.error) {
            if (data.error[key].hasOwnProperty("error")) {
              if (key === "address") {
                editVenueRef.current?.setAddressErrors?.(data.error[key].error || data.error[key]);
              }
              setPostError(data.error[key].error);
            }
          }
        }
      } else {
        setPostError(null);
        refreshAll?.(selectedVenue?.marker);
      }
    } catch (error) {
      console.log(error);
    }

    setLoading(false);
  };

  if (!userVenues || userVenues.length === 0) {
    return (
      <div className="flex flex-col gap-kit-16 md:max-w-[36rem]">
        <div className="mt-kit-24 px-kit-16 text-kit-base font-['Primary-Medium'] text-[var(--inyc-primary-text-color)]">
          these venues are linked to your account and are available for you to edit
        </div>
        <div className="-mt-[6px] px-kit-16">
          <div className="mb-kit-2 border-b-[1px] border-[color:color-mix(in_srgb,var(--inyc-outline-color)_35%,transparent)]" />
        </div>
        {refreshControl}
        <div className="rounded-kit-xl border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] [:is(body.dark)_&]:bg-[var(--inyc-card-bg)] p-kit-32 text-center">
          <div className="text-kit-base font-['Primary-Medium'] text-[var(--inyc-primary-text-color-light)]">
            no claimed venues
          </div>
          <div className="mt-kit-8 text-kit-xs leading-[1.3] text-[var(--inyc-primary-text-color-light)]">
            to claim a venue, go to the{" "}
            <span className="font-['Primary-Bold'] text-[var(--inyc-primary-text-color)]">
              "new"
            </span>{" "}
            tab
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-kit-16 md:max-w-[36rem]">
        <div className="mt-kit-24 px-kit-16 text-kit-base font-['Primary-Medium'] text-[var(--inyc-primary-text-color)]">
          these venues are linked to your account and are available for you to edit
        </div>
        <div className="-mt-[6px] px-kit-16">
          <div className="mb-kit-2 border-b-[1px] border-[color:color-mix(in_srgb,var(--inyc-outline-color)_35%,transparent)]" />
        </div>
        {refreshControl}

      <div className="grid gap-kit-12">
        {formattedVenues.map((venue) => {
          const isSelected = venue._id === selectedVenue?.venue?._id;

          return (
            <React.Fragment key={venue._id}>
              <article
                role="button"
                tabIndex={0}
                onClick={() => handleVenuPick(venue)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleVenuPick(venue);
                  }
                }}
                className={`claimed-venue-card w-full max-w-full overflow-hidden cursor-pointer rounded-kit-xl border-[1px] bg-[var(--inyc-primary)] p-kit-16 shadow-kit-card transition-all duration-200 hover:border-[var(--inyc-secondary)] ${
                  isSelected
                    ? "border-[var(--inyc-secondary)]"
                    : "border-[var(--inyc-input-border-color)]"
                }`}
              >
                <div className="flex min-w-0 items-stretch justify-between gap-kit-8">
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-kit-10 overflow-hidden">
                      <h3 className="m-0 min-w-0 truncate text-kit-md font-['Primary-Bold'] leading-tight text-[var(--inyc-primary-text-color)]">
                        {venue.name}
                      </h3>
                      <span className="claimed-status-pill flex h-[22px] w-[70px] shrink-0 items-center justify-center rounded-kit-full border-[1px] text-center text-kit-2xs font-['Primary-Medium']">
                        claimed
                      </span>
                    </div>
                    <div className="mt-kit-6 text-kit-sm leading-relaxed text-[var(--inyc-primary-text-color)]">
                      {venue.address}
                    </div>

                    <div className="mt-kit-2 flex flex-nowrap items-baseline gap-x-kit-12 text-kit-sm text-[var(--inyc-primary-text-color-light)] md:gap-x-kit-16">
                      <div className="flex min-w-0 shrink items-baseline gap-kit-8 whitespace-nowrap">
                        <div className="shrink-0 font-['Primary-Medium'] text-[var(--inyc-primary-text-color)]">
                          created:
                        </div>
                        <div className="min-w-0 truncate text-kit-2xs uppercase tracking-[0.08em] opacity-60">
                          <span className="md:hidden">{venue.createdAtCompact}</span>
                          <span className="hidden md:inline">{venue.createdAt}</span>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-baseline gap-kit-8 whitespace-nowrap">
                        <div className="shrink-0 font-['Primary-Medium'] text-[var(--inyc-primary-text-color)]">
                          type:
                        </div>
                        <div className="text-kit-2xs lowercase tracking-[0.08em] text-[var(--inyc-primary-text-color)]">
                          {venue.venueType}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex w-auto shrink-0 flex-col items-end justify-between self-stretch md:justify-center md:gap-kit-8 md:self-center">
                    <button
                      type="button"
                      className="claimed-post-event-pill flex h-[28px] w-[87px] items-center justify-center whitespace-nowrap rounded-kit-full border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] text-kit-2xs font-['Primary-Medium'] text-[var(--inyc-primary-text-color)]"
                      onClick={(e) => {
                        e.stopPropagation();
                        nav(`/dashboard?tab=new-event&venue=${venue._id}`);
                      }}
                    >
                      post event
                    </button>
                    <button
                      type="button"
                      className="claimed-edit-venue-pill flex h-[28px] w-[87px] items-center justify-center rounded-kit-full border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] text-center text-kit-2xs font-['Primary-Medium'] text-[var(--inyc-primary-text-color)]"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleVenuPick(venue);
                      }}
                    >
                      edit
                    </button>
                  </div>
                </div>
              </article>
              {isSelected && (
                <div className="relative rounded-kit-xl border-[1px] border-[var(--inyc-input-border-color)] bg-[var(--inyc-primary)] p-kit-16">
                  <button
                    type="button"
                    aria-label="close edit venue"
                    className="absolute right-[3.5rem] top-[1.05rem] flex h-[2.5rem] w-[2.5rem] items-center justify-center rounded-kit-full border-none bg-transparent text-[var(--inyc-primary-text-color)] transition-all duration-200 hover:bg-[var(--inyc-input-background-color)]"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedVenue({});
                    }}
                  >
                    <IoClose size={24} />
                  </button>
                  <div
                    className="mb-kit-2 text-center font-['Primary-Medium'] text-[var(--inyc-primary-text-color)]"
                    style={{ fontSize: "calc(var(--inyc-input-label-size, 1.3rem) + 3pt)" }}
                  >
                    edit venue
                  </div>
                  <EditVenue
                    ref={editVenueRef}
                    venues={venues}
                    refreshVenues={refreshVenues}
                    userLocation={userLocation}
                    handleVenuPick={handleVenuPick}
                    handleInputChange={handleInputChange}
                    selectedVenue={selectedVenue}
                    setSelectedVenue={setSelectedVenue}
                    alignFieldsToAddress
                    compactTop
                  />
                  <div className="mx-auto mt-0 mb-kit-16 flex w-[90%] flex-col justify-center">
                    <Button
                      error={postError}
                      prompt
                      message="confirm?"
                      className="mx-auto w-fit"
                      type="secondary"
                      btnClassName="!flex !items-center !justify-center !w-[147px] !whitespace-nowrap !py-kit-4 !px-kit-20 !leading-[1.2]"
                      loading={loading}
                      onClick={updateVenue}
                    >
                      update venue
                    </Button>
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default ClaimedVenues;
